import React, { useContext } from "react";
import { SocialShare } from "@bloomberg/media-ui-core";
import {
  useRegUIClient,
  type RegUISessionDetails,
  type UserInfo,
} from "@foundation/reg-ui-client";
import {
  copyLinkHandler,
  emailShareHandler,
  facebookShareHandler,
  linkedInShareHandler,
  twitterShareHandler,
} from "./handlers";
import styles from "./Share.module.css";
import { QuoteContext } from "~context/QuoteContext";
import { getDescriptionAndTitle } from "~utils";
import { getCanonicalUrl, getDisplayName } from "~utils/seo/structuredData";
import { isEnabled } from "~utils/features";

const SOCIAL_SHARE_ENABLED = isEnabled("enableSocialShare");

type ShareProps = {
  className?: string;
  showLabel?: boolean;
  vertical?: boolean;
  fixed?: boolean;
};

type SocialClickHandler = () => void | ((event: Event) => void);

export default function Share(props: ShareProps) {
  const { className, showLabel = false, vertical = false } = props;

  const quoteData = useContext(QuoteContext);
  const { session } = useRegUIClient();

  const { userInfo } = session || ({} as RegUISessionDetails);

  if (!SOCIAL_SHARE_ENABLED || !quoteData?.id) {
    return null;
  }

  const {
    type,
    name,
    longName,
    id,
    primaryExchange,
    securityName,
    mediaSecuritySubtype,
  } = quoteData;

  const displayName = getDisplayName(type, name, longName);
  const canonical = getCanonicalUrl(id);

  const { title: shareText } = getDescriptionAndTitle(
    type,
    displayName,
    id,
    primaryExchange,
    securityName,
    mediaSecuritySubtype
  );

  return (
    <div className={className}>
      <SocialShare
        label="Share this market quote"
        showLabel={showLabel}
        vertical={vertical}
        copiedToastClassName={styles.copiedToast}
        socials={{
          email: ((event: Event) =>
            emailShareHandler({
              canonical,
              shareText,
              event,
              userInfo: userInfo as UserInfo,
            })) as SocialClickHandler,
          facebook: ((event: Event) =>
            facebookShareHandler({
              canonical,
              event,
              userInfo: userInfo as UserInfo,
            })) as SocialClickHandler,
          link: (() =>
            copyLinkHandler({
              canonical,
            })) as SocialClickHandler,
          linkedin: ((event: Event) =>
            linkedInShareHandler({
              canonical,
              shareText,
              event,
              userInfo: userInfo as UserInfo,
            })) as SocialClickHandler,
          twitter: ((event: Event) =>
            twitterShareHandler({
              canonical,
              shareText,
              event,
              userInfo: userInfo as UserInfo,
            })) as SocialClickHandler,
        }}
      />
    </div>
  );
}
